import React, { useRef } from 'react'
import cx from 'classnames'
import styles from './ScrollSlider.module.scss'

export interface ScrollSliderProps {
  children: React.ReactNode
  className?: string
  gap?: number
}

const DRAG_THRESHOLD = 5

const ScrollSlider: React.FC<ScrollSliderProps> = ({ children, className, gap = 16 }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const hasDraggedRef = useRef(false)
  const startXRef = useRef<number | null>(null)

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return

    scrollRef.current.style.cursor = 'grabbing'
    hasDraggedRef.current = false
    startXRef.current = e.clientX
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    if (!el || startXRef.current === null) return

    const deltaX = e.clientX - startXRef.current

    if (Math.abs(deltaX) > DRAG_THRESHOLD) {
      hasDraggedRef.current = true
    }

    if (hasDraggedRef.current) {
      el.scrollLeft -= e.movementX
    }
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return

    scrollRef.current.style.cursor = 'grab'

    // Cancel link activation only after a real drag
    if (hasDraggedRef.current) {
      // Prevent any default behavior for the pointerup event
      e.preventDefault()

      // Also add a global click handler to prevent clicks on ANY element
      // that occur after a drag
      const handleGlobalClick = (clickEvent: MouseEvent) => {
        clickEvent.preventDefault()
        clickEvent.stopPropagation()
        document.removeEventListener('click', handleGlobalClick, true)
      }

      // Use capture phase to catch the click before it reaches the target
      document.addEventListener('click', handleGlobalClick, true)

      // Remove the global handler after a short time in case click doesn't fire
      setTimeout(() => {
        document.removeEventListener('click', handleGlobalClick, true)
      }, 50)
    }

    startXRef.current = null
  }

  return (
    <div
      ref={scrollRef}
      className={cx(styles.scrollSlider, className)}
      style={{ gap: `${gap}px` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {children}
    </div>
  )
}

export default ScrollSlider
